import React, { useEffect, useState, useMemo } from "react";
import {
  Heart,
  Activity,
  Droplet,
  Beaker,
  HeartPulse,
  Search,
  Calendar,
  FlaskConical,
  Thermometer,
  Weight,
  Ruler,
  RulerDimensionLine,
  X,
  Eye,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Employee, HealthCheck, HealthHistoryProps } from "../../types";
import {
  BODY_METRICS,
  ASSESSMENT_STYLE,
  assess,
  getBodyValue,
  getMetricRange,
  getSkeletalMassRangeKg,
  PersonProfile,
} from "./bodyComposition";
import { authHeaders } from "../../lib/auth";

const ITEMS_PER_PAGE = 3;
const ROWS_PER_PAGE = 9;

const calculateBMI = (weight?: number, height?: number): number | null => {
  if (!weight || !height) return null;
  const heightM = height / 100;
  if (heightM <= 0) return null;
  return weight / (heightM * heightM);
};

const getBMICategory = (bmi: number): { label: string; color: string } => {
  if (bmi < 18.5) return { label: "Kurus", color: "text-blue-600" };
  if (bmi < 25) return { label: "Ideal", color: "text-green-600" };
  if (bmi < 30) return { label: "Gemuk", color: "text-amber-600" };
  return { label: "Obesitas", color: "text-red-600" };
};

const FAT_MASS_NORMAL = { min: 11.1, max: 17.3 };
const WEIGHT_NORMAL = { min: 40.16, max: 54.38, low: 0.1, high: 0.12 };
const BODY_FAT_NORMAL = { min: 18, max: 28, low: 0.3, high: 0.3 };
const BMI_NORMAL = { min: 18.5, max: 24.9 };

const calculateAge = (birthDate: string): number | null => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const RangeBar: React.FC<{
  label: string;
  value: number | null;
  min: number;
  max: number;
  domainMin: number;
  domainMax: number;
  unit: string;
  low?: number;
  high?: number;
  tickStep?: number;
}> = ({
  label,
  value,
  min,
  max,
  domainMin,
  domainMax,
  unit,
  low,
  high,
  tickStep,
}) => {
  const span = domainMax - domainMin;

  // Posisi batas Normal
  const normalStart = ((min - domainMin) / span) * 100;
  const normalEnd = ((max - domainMin) / span) * 100;

  // Posisi nilai
  const pos = value === null ? null : ((value - domainMin) / span) * 100;

  const category = value === null ? null : assess(value, min, max, low, high);

  // Membuat angka pada bagian bawah
  const ticks: number[] = [];

  if (tickStep && tickStep > 0) {
    for (let t = domainMin; t <= domainMax + 1e-9; t += tickStep) {
      ticks.push(t);
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="font-medium text-gray-700">{label}</span>

        <span className="flex items-center gap-2">
          <span className="text-gray-600 font-bold">
            {value === null ? "—" : unit ? `${value} ${unit}` : value}
          </span>

          {category && (
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                ASSESSMENT_STYLE[category]
              }`}
            >
              {category}
            </span>
          )}
        </span>
      </div>

      {/* Label Rendah - Normal - Tinggi */}
      <div className="relative h-5 text-xs font-medium mb-0">
        {/* Rendah */}
        <div
          className="absolute top-0 bottom-0 flex items-center justify-center bg-blue-100 text-blue-600"
          style={{
            left: "0%",
            width: `${normalStart}%`,
          }}
        >
          Rendah
        </div>

        {/* Normal */}
        <div
          className="absolute top-0 bottom-0 flex items-center justify-center bg-green-100 text-green-600"
          style={{
            left: `${normalStart}%`,
            width: `${normalEnd - normalStart}%`,
          }}
        >
          Normal
        </div>

        {/* Tinggi */}
        <div
          className="absolute top-0 bottom-0 flex items-center justify-center bg-orange-100 text-orange-600"
          style={{
            left: `${normalEnd}%`,
            width: `${100 - normalEnd}%`,
          }}
        >
          Tinggi
        </div>
      </div>

      {/* Range bar */}
      <div className="relative h-3 overflow-hidden">
        {/* Rendah */}
        <div
          className="absolute top-0 bottom-0 bg-blue-400"
          style={{
            left: "0%",
            width: `${normalStart}%`,
          }}
        />

        {/* Normal */}
        <div
          className="absolute top-0 bottom-0 bg-green-500"
          style={{
            left: `${normalStart}%`,
            width: `${normalEnd - normalStart}%`,
          }}
        />

        {/* Tinggi */}
        <div
          className="absolute top-0 bottom-0 bg-orange-400"
          style={{
            left: `${normalEnd}%`,
            width: `${100 - normalEnd}%`,
          }}
        />

        {/* Indicator nilai */}
        {pos !== null && (
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1.5 h-5 bg-gray-900 rounded-full z-10"
            style={{
              left: `${Math.max(0, Math.min(100, pos))}%`,
            }}
          />
        )}
      </div>

      {/* Ticks */}
      <div className="relative h-5 mt-1">
        {ticks.map((t, i) => {
          const pct = ((t - domainMin) / span) * 100;

          const isFirst = i === 0;
          const isLast = i === ticks.length - 1;

          return (
            <div
              key={t}
              className="absolute top-0 flex flex-col items-center"
              style={{
                left: `${pct}%`,
                alignItems: isFirst
                  ? "flex-start"
                  : isLast
                    ? "flex-end"
                    : "center",
                transform: isFirst
                  ? "none"
                  : isLast
                    ? "translateX(-100%)"
                    : "translateX(-50%)",
              }}
            >
              <div className="h-1.5 w-px bg-gray-300" />

              <div className="text-[10px] leading-none text-gray-500 whitespace-nowrap">
                {t}
              </div>
            </div>
          );
        })}
      </div>

      {/* Normal range */}
      <div className="text-xs text-gray-400 mt-1">
        Normal: {min} - {max}
        {unit ? ` ${unit}` : ""}
      </div>
    </div>
  );
};

export const HealthHistory: React.FC<HealthHistoryProps> = ({
  refreshTrigger,
}) => {
  const [checks, setChecks] = useState<HealthCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedCheck, setSelectedCheck] = useState<HealthCheck | null>(null);
  const [profiles, setProfiles] = useState<Record<string, Employee>>({});

  const fetchChecks = async () => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/api/health/checks`,
        { headers: authHeaders() },
      );
      const data = await res.json();
      setChecks(data);
    } catch (err) {
      console.error("❌ Fetch health checks error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/api/employees`,
        { headers: authHeaders() },
      );
      const data: Employee[] = await res.json();
      const map: Record<string, Employee> = {};
      data.forEach((e) => {
        map[e.name] = e;
      });
      setProfiles(map);
    } catch (err) {
      console.error("❌ Fetch employees error:", err);
    }
  };

  useEffect(() => {
    fetchChecks();
    fetchEmployees();
  }, [refreshTrigger]);

  const dateOptions = useMemo(() => {
    const months = new Set<string>();
    checks.forEach((check) => {
      const date = new Date(check.measured_at);
      const monthStr = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}`;
      months.add(monthStr);
    });

    return Array.from(months).sort().reverse();
  }, [checks]);

  const filteredChecks = useMemo(() => {
    return checks.filter((h) => {
      const nama = h.employee_name.toLowerCase();
      const tanggal = new Date(h.measured_at).toLocaleDateString("id-ID");
      const keyword = search.toLowerCase();
      const searchMatch = nama.includes(keyword) || tanggal.includes(keyword);
      const monthMatch =
        !selectedDate || h.measured_at.startsWith(selectedDate);
      return searchMatch && monthMatch;
    });
  }, [checks, search, selectedDate]);

  const totalPages = Math.ceil(filteredChecks.length / ITEMS_PER_PAGE);
  const paginatedChecks = filteredChecks.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages]);

  const loadImageAsDataURL = (src: string): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = src;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("No canvas context");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (e) => reject(e);
    });

  const handlePrint = async () => {
    if (!selectedDate) {
      alert("Pilih bulan terlebih dahulu!");
      return;
    }

    const selectedDateObj = new Date(selectedDate + "-01T00:00:00");
    const selectedDateLocale = selectedDateObj.toLocaleDateString("id-ID", {
      month: "long",
      year: "numeric",
    });

    const selectedDateData = checks.filter((row) =>
      row.measured_at.startsWith(selectedDate),
    );

    if (selectedDateData.length === 0) {
      alert(`Tidak ada data untuk bulan ${selectedDateLocale}!`);
      return;
    }

    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const bottomMargin = 20;

      let imgData: string | null = null;
      try {
        imgData = await loadImageAsDataURL(
          `${import.meta.env.VITE_API_BASE}/template.png`,
        );
      } catch {
        console.warn(
          "Template image gagal di-load, PDF tetap dibuat tanpa background.",
        );
      }

      for (let i = 0; i < selectedDateData.length; i += ROWS_PER_PAGE) {
        if (i > 0) doc.addPage();
        if (imgData) doc.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);

        if (i === 0) {
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.text("DAFTAR HADIR", pageWidth / 2, 60, { align: "center" });
          doc.text("SOSIALISASI K3", pageWidth / 2, 68, { align: "center" });

          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text(`Malang, ${selectedDateLocale}`, pageWidth - 20, 80, {
            align: "right",
          });
        }

        const chunk = selectedDateData.slice(i, i + ROWS_PER_PAGE);

        autoTable(doc, {
          startY: i === 0 ? 82 : 50,
          head: [
            [
              "No",
              "Nama",
              "Tensi",
              "Detak Jantung",
              "Suhu",
              "Berat Badan",
              "Gula Darah",
              "Kolesterol",
              "Asam Urat",
              "TTD",
            ],
          ],
          body: chunk.map((row, idx) => {
            const nomor = i + idx + 1;

            return [
              nomor,
              row.employee_name,
              row.blood_pressure_systolic && row.blood_pressure_diastolic
                ? `${row.blood_pressure_systolic}/${row.blood_pressure_diastolic}`
                : "—",
              row.heart_rate ?? "—",
              row.temperature ?? "—",
              row.weight ?? "—",
              row.blood_sugar ?? "—",
              row.cholesterol ?? "—",
              row.urid_acid ?? "—",
              "",
            ];
          }),
          theme: "grid",
          styles: {
            fontSize: 9,
            minCellHeight: 16,
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.2,
            valign: "middle",
          },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 45 },
            2: { cellWidth: 20 },
            3: { cellWidth: 18 },
            4: { cellWidth: 15 },
            5: { cellWidth: 15 },
            6: { cellWidth: 15 },
            7: { cellWidth: 19 },
            8: { cellWidth: 15 },
            9: { cellWidth: 20 },
          },
          headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
          margin: { left: 10, right: 10 },
          didDrawCell: function (data) {
            if (data.section === "body" && data.column.index === 9) {
              const rowIndex = data.row.index;
              const rowData = chunk[rowIndex];

              if (rowData?.signature_data) {
                try {
                  const imgWidth = 22;
                  const imgHeight = 9;
                  const x = data.cell.x + (data.cell.width - imgWidth) / 2;
                  const y = data.cell.y + (data.cell.height - imgHeight) / 2;

                  doc.addImage(
                    rowData.signature_data,
                    "PNG",
                    x,
                    y,
                    imgWidth,
                    imgHeight,
                  );
                } catch (err) {
                  console.warn("Gagal menambahkan tanda tangan:", err);
                  doc.setFontSize(8);
                  doc.text(
                    "✓",
                    data.cell.x + data.cell.width / 2,
                    data.cell.y + data.cell.height / 2,
                    { align: "center", baseline: "middle" },
                  );
                }
              }
            }
          },
        });

        const isLastPage = i + ROWS_PER_PAGE >= selectedDateData.length;
        if (isLastPage) {
          let finalY = (doc as any).lastAutoTable.finalY || 80;

          if (finalY + 70 > pageHeight - bottomMargin) {
            doc.addPage();
            if (imgData)
              doc.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
            finalY = 40;
          }

          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text(
            `Malang, ${selectedDateLocale}`,
            pageWidth - 20,
            finalY + 20,
            {
              align: "right",
            },
          );

          doc.setFont("helvetica", "bold");
          doc.text("ACHMAD ROFIUDDIN H.F.", pageWidth - 20, finalY + 50, {
            align: "right",
          });
          doc.setFont("helvetica", "normal");
          doc.text("SAFETY MAN", pageWidth - 30, finalY + 56, {
            align: "right",
          });
          doc.text("PT TEKNO KLOP INDONESIA", pageWidth - 20, finalY + 62, {
            align: "right",
          });
        }
      }

      const pdfBlob = doc.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank");
    } catch (err) {
      console.error("❌ Gagal generate PDF", err);
      alert("Gagal generate PDF. Cek console untuk detail.");
    }
  };

  if (loading)
    return <p className="text-gray-500">Loading data kesehatan...</p>;
  if (checks.length === 0)
    return <p className="text-gray-500">Belum ada data kesehatan</p>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Heart className="h-5 w-5 mr-2 text-red-600" /> Riwayat Pemeriksaan
        </h3>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Date Picker */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Pilih Bulan</option>
              {dateOptions.map((monthStr) => {
                const [year, month] = monthStr.split("-").map(Number);
                const dateObj = new Date(year, month - 1, 1);
                const dateLabel = dateObj.toLocaleDateString("id-ID", {
                  month: "long",
                  year: "numeric",
                });
                return (
                  <option key={monthStr} value={monthStr}>
                    {dateLabel}
                  </option>
                );
              })}
            </select>
          </div>

          <button
            onClick={handlePrint}
            disabled={!selectedDate}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg shadow hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Calendar className="h-4 w-4" />
            Print PDF
          </button>
        </div>
      </div>
      <div className="flex items-center mb-4 border rounded-lg px-3 py-2">
        <Search className="h-4 w-4 text-gray-400 mr-2" />
        <input
          type="text"
          placeholder="Cari nama atau tanggal (dd/mm/yyyy)"
          className="w-full outline-none text-sm"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>
      <div className="mb-4 flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
        <span className="text-sm font-semibold text-gray-700">
          Total Data: {filteredChecks.length}
        </span>
        {selectedDate && (
          <span className="text-sm text-gray-500">
            Bulan:{" "}
            {(() => {
              const [year, month] = selectedDate.split("-").map(Number);
              return new Date(year, month - 1, 1).toLocaleDateString("id-ID", {
                month: "long",
                year: "numeric",
              });
            })()}
          </span>
        )}
      </div>
      <div className="mb-4 rounded-xl border bg-gradient-to-r from-blue-50 to-white p-4">
        <h4 className="font-semibold text-gray-800 mb-3">
          Keterangan Parameter Kesehatan
        </h4>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            {
              icon: <Activity className="h-5 w-5 text-pink-500" />,
              label: "Detak Jantung",
            },
            {
              icon: <HeartPulse className="h-5 w-5 text-red-500" />,
              label: "Tekanan Darah",
            },
            {
              icon: <Thermometer className="h-5 w-5 text-orange-500" />,
              label: "Suhu Tubuh",
            },
            {
              icon: <Weight className="h-5 w-5 text-blue-500" />,
              label: "Berat Badan",
            },
            {
              icon: <RulerDimensionLine className="h-5 w-5 text-blue-500" />,
              label: "Tinggi Badan",
            },
            {
              icon: <Ruler className="h-5 w-5 text-cyan-500" />,
              label: "BMI (Indeks Massa Tubuh)",
            },
            {
              icon: <Droplet className="h-5 w-5 text-green-500" />,
              label: "Gula Darah",
            },
            {
              icon: <Beaker className="h-5 w-5 text-purple-500" />,
              label: "Kolesterol",
            },
            {
              icon: <FlaskConical className="h-5 w-5 text-amber-500" />,
              label: "Asam Urat",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-3 rounded-lg border bg-white p-3 shadow-sm hover:shadow-md transition"
            >
              {item.icon}
              <span className="text-sm font-medium text-gray-700">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
        {paginatedChecks.map((h) => (
          <div
            key={h.id}
            className="p-4 border rounded-lg hover:bg-gray-50 transition"
          >
            <div className="flex items-center justify-between">
              <div className="font-medium text-gray-900">{h.employee_name}</div>
              <div className="text-sm text-gray-500">
                {new Date(h.measured_at).toLocaleString("id-ID")}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3 text-sm">
              <div className="flex items-center text-gray-700">
                <Activity className="h-4 w-4 mr-2 text-pink-500" />
                <span>{h.heart_rate ? `${h.heart_rate} bpm` : "—"}</span>
              </div>

              <div className="flex items-center text-gray-700">
                <HeartPulse className="h-4 w-4 mr-2 text-red-500" />
                <span>
                  {h.blood_pressure_systolic && h.blood_pressure_diastolic
                    ? `${h.blood_pressure_systolic}/${h.blood_pressure_diastolic} mmHg`
                    : "—"}
                </span>
              </div>

              <div className="flex items-center text-gray-700">
                <Thermometer className="h-4 w-4 mr-2 text-orange-500" />
                <span>{h.temperature ? `${h.temperature} °C` : "—"}</span>
              </div>

              <div className="flex items-center text-gray-700">
                <Weight className="h-4 w-4 mr-2 text-blue-500" />
                <span>{h.weight ? `${h.weight} kg` : "—"}</span>
              </div>

              <div className="flex items-center text-gray-700">
                <RulerDimensionLine className="h-4 w-4 mr-2 text-blue-500" />
                <span>{h.height ? `${h.height} cm` : "—"}</span>
              </div>

              <div className="flex items-center text-gray-700">
                <Ruler className="h-4 w-4 mr-2 text-cyan-500" />
                {(() => {
                  const bmi = calculateBMI(h.weight, h.height);
                  if (bmi === null) return <span>—</span>;
                  const { label, color } = getBMICategory(bmi);
                  return (
                    <span>
                      BMI {bmi.toFixed(1)}{" "}
                      <span className={`font-medium ${color}`}>({label})</span>
                    </span>
                  );
                })()}
              </div>

              <div className="flex items-center text-gray-700">
                <Droplet className="h-4 w-4 mr-2 text-green-500" />
                <span>{h.blood_sugar ? `${h.blood_sugar} mg/dL` : "—"}</span>
              </div>

              <div className="flex items-center text-gray-700">
                <Beaker className="h-4 w-4 mr-2 text-purple-500" />
                <span>{h.cholesterol ? `${h.cholesterol} mg/dL` : "—"}</span>
              </div>

              <div className="flex items-center text-gray-700">
                <FlaskConical className="h-4 w-4 mr-2 text-amber-500" />
                <span>{h.urid_acid ? `${h.urid_acid} mg/dL` : "—"}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelectedCheck(h)}
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium hover:text-blue-700"
            >
              <Eye className="h-4 w-4" />
              Lihat Komposisi Tubuh
            </button>
            {h.notes && (
              <p className="text-gray-500 text-sm mt-2 italic">
                Catatan: {h.notes}
              </p>
            )}
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-4 space-x-1">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(
            (pageNumber) => (
              <button
                key={pageNumber}
                onClick={() => setPage(pageNumber)}
                className={`px-3 py-1 rounded-lg border text-sm ${
                  page === pageNumber
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-blue-100"
                }`}
              >
                {pageNumber}
              </button>
            ),
          )}
        </div>
      )}
      {selectedCheck &&
        (() => {
          const c = selectedCheck;
          const emp = profiles[c.employee_name];
          const profile: PersonProfile = {
            gender: emp?.gender ?? null,
            age: emp?.birth_date ? calculateAge(emp.birth_date) : null,
            height: c.height ?? emp?.height ?? null,
          };
          const round1 = (n: number) => Math.round(n * 10) / 10;
          const weight = c.weight ?? null;
          const bodyFat = getBodyValue(c, "body_fat");
          const skeletalPct = getBodyValue(c, "skeletal_muscle");
          const bmi = calculateBMI(c.weight, c.height);
          const skeletalMassRaw =
            weight != null && skeletalPct != null
              ? (weight * skeletalPct) / 100
              : null;
          const skeletalMass =
            skeletalMassRaw === null ? null : round1(skeletalMassRaw);
          const fatMass =
            weight != null && bodyFat != null
              ? round1((weight * bodyFat) / 100)
              : null;
          const bmiValue = bmi === null ? null : round1(bmi);
          const bodyFatValue = bodyFat === null ? null : round1(bodyFat);

          const weightRange = getMetricRange(profile, "weight") ?? {
            min: WEIGHT_NORMAL.min,
            max: WEIGHT_NORMAL.max,
          };
          const bodyFatRange = getMetricRange(profile, "body_fat") ?? {
            min: BODY_FAT_NORMAL.min,
            max: BODY_FAT_NORMAL.max,
          };
          const skeletalMassRange = getSkeletalMassRangeKg(profile);
          const fatMassRange =
            weight != null && bodyFatRange
              ? {
                  min: round1((weight * bodyFatRange.min) / 100),
                  max: round1((weight * bodyFatRange.max) / 100),
                }
              : FAT_MASS_NORMAL;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setSelectedCheck(null)}
              />
              <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Komposisi Tubuh
                    </h3>
                    <p className="text-sm text-gray-500">{c.employee_name}</p>
                    <p className="text-xs text-gray-400">
                      {emp?.birth_date ? (
                        `Umur: ${calculateAge(emp.birth_date)} tahun`
                      ) : (
                        <span className="text-red-600">Umur belum diset</span>
                      )}
                      {c.height || emp?.height
                        ? ` · Tinggi: ${c.height ?? emp?.height} cm`
                        : ""}
                      {emp?.gender
                        ? ` · Jenis Kelamin: ${emp?.gender === "male" ? "Laki-laki" : "Perempuan"}`
                        : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedCheck(null)}
                    className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="py-2 pr-4 font-medium">Parameter</th>
                        <th className="py-2 pr-4 font-medium">
                          Nilai Pengukuran
                        </th>
                        <th className="py-2 pr-4 font-medium">Asesmen</th>
                        <th className="py-2 font-medium">Normal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {BODY_METRICS.map((m) => {
                        const value = getBodyValue(c, m.key);

                        const range = getMetricRange(
                          profile,
                          m.key,
                          weight,
                        ) ?? {
                          min: m.min,
                          max: m.max,
                        };

                        const assessment =
                          value === null
                            ? null
                            : assess(
                                value,
                                range.min,
                                range.max,
                                m.low,
                                m.high,
                              );

                        return (
                          <tr key={m.key} className="border-b border-gray-100">
                            <td className="py-2 pr-4 text-gray-900 font-medium">
                              {m.label} ({m.unit})
                            </td>

                            <td className="py-2 pr-4 text-gray-700">
                              {value === null ? "—" : `${value}`}
                            </td>

                            <td className="py-2 pr-4">
                              {assessment ? (
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    ASSESSMENT_STYLE[assessment]
                                  }`}
                                >
                                  {assessment}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>

                            <td className="py-2 text-gray-700">
                              {range.min} - {range.max}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-4">
                    Analisis Otot dan Lemak
                  </h4>
                  <div className="space-y-5">
                    <RangeBar
                      label="Berat"
                      value={weight}
                      min={weightRange.min}
                      max={weightRange.max}
                      domainMin={0}
                      domainMax={150}
                      unit="kg"
                      low={WEIGHT_NORMAL.low}
                      high={WEIGHT_NORMAL.high}
                      tickStep={25}
                    />
                    {skeletalMassRange && (
                      <RangeBar
                        label="Otot Rangka"
                        value={skeletalMass}
                        min={skeletalMassRange.min}
                        max={skeletalMassRange.max}
                        domainMin={0}
                        domainMax={100}
                        unit="kg"
                        tickStep={20}
                      />
                    )}
                    <RangeBar
                      label="Massa Lemak"
                      value={fatMass}
                      min={fatMassRange.min}
                      max={fatMassRange.max}
                      domainMin={0}
                      domainMax={30}
                      unit="kg"
                      tickStep={5}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-4">
                    Analisis Kegemukan
                  </h4>
                  <div className="space-y-5">
                    <RangeBar
                      label="BMI"
                      value={bmiValue}
                      min={BMI_NORMAL.min}
                      max={BMI_NORMAL.max}
                      domainMin={0}
                      domainMax={50}
                      unit=""
                      tickStep={10}
                    />
                    <RangeBar
                      label="Lemak"
                      value={bodyFatValue}
                      min={bodyFatRange.min}
                      max={bodyFatRange.max}
                      domainMin={0}
                      domainMax={75}
                      unit="%"
                      low={BODY_FAT_NORMAL.low}
                      high={BODY_FAT_NORMAL.high}
                      tickStep={15}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default HealthHistory;
